import { useEffect, useRef } from 'react';

import { useConfirm } from '@contexts/Confirm.context';
import { useToast } from '@contexts/Toast.context';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';

/**
 * Asks before the object tree switches object while the open tile (or image)
 * has unsaved changes: "Save and switch" saves the tile and lets the switch go
 * ahead, "Stay" keeps the current object and the tile open. A tile with nothing
 * unsaved switches at once, as before. Only the active object's changes count: they
 * are what its Save sends, and the other objects' boxes stay on the map through a
 * switch. When the tile cannot be saved yet (not read), a toast says so and the
 * switch waits; a read that failed is told apart from one still running. A pick made
 * while a save is under way is not acted on, and a toast says to pick again. The guard is registered in the session store, where the tree asks
 * it before switching.
 *
 * @param {object} options
 * @param {boolean} options.enabled - an object session (Collection view) is running
 * @param {{ saving: boolean, hasUnsavedChanges: (options?: { activeOnly?: boolean }) => boolean,
 *   canSaveOpenTile: () => boolean, isOpenTileReading: () => boolean, saveTile: () => Promise<boolean> }} options.session
 * @param {boolean} options.isOpen - a tile (or image) is open
 * @param {string} [options.objectName] - the object being annotated, for the question
 * @param {'tile'|'image'} [options.unit]
 */
export const useObjectSwitchGuard = ({ enabled, session, isOpen, objectName, unit = 'tile' }) => {
	const { confirm } = useConfirm();
	const { addToast } = useToast();

	// The guard outlives the render that registered it; it reads the latest values here.
	const latest = useRef({});
	latest.current = { session, isOpen, objectName, unit, confirm, addToast };

	useEffect(() => {
		if (!enabled) return undefined;
		const guard = async next => {
			const {
				session: current,
				isOpen: open,
				objectName: name,
				unit: noun,
				confirm: ask,
				addToast: toast,
			} = latest.current;
			// A save is under way: the switch waits for the user to pick again.
			if (current.saving) {
				toast({
					id: 'annotation-switch-saving',
					bg: 'warning',
					title: 'Saving…',
					message: 'Pick the object again when the save is done.',
				});
				return false;
			}
			if (!open || !current.hasUnsavedChanges({ activeOnly: true })) return true;
			// Save could not act: say why rather than offer it. A read still running is worth
			// waiting for; one that failed is not, so that case says how to get out.
			if (!current.canSaveOpenTile()) {
				toast({
					id: 'annotation-switch-unsaved',
					bg: 'warning',
					title: `This ${noun} cannot be saved yet`,
					message: current.isOpenTileReading()
						? 'It is still being read. Wait a moment, then save it before switching.'
						: 'It could not be read from the server. Open it again to retry, or leave it to discard the changes.',
				});
				return false;
			}
			const confirmed = await ask({
				title: `Unsaved changes in this ${noun}`,
				message: `The changes in this ${noun} for ${name ?? 'this object'} are not saved. Save them before switching to ${next?.name ?? 'another object'}?`,
				confirmLabel: 'Save and switch',
				cancelLabel: 'Stay',
			});
			if (!confirmed) return false;
			// A failed save keeps the current object; the save itself says why.
			return current.saveTile();
		};
		const { setObjectSwitchGuard } = useAnnotationSessionStore.getState();
		setObjectSwitchGuard(guard);
		return () => {
			if (useAnnotationSessionStore.getState().objectSwitchGuard === guard) {
				setObjectSwitchGuard(null);
			}
		};
	}, [enabled]);
};
